import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Grid, InputAdornment, Switch } from '@material-ui/core';

import RequestPageLayout from '../common/request-page-layout';
import RequestSection from '../common/request-section';
import RequestInput from '../common/request-input';
import RequestField from '../common/request-field';
import AgentEntityAutocomplete from '../common/agent-entity-autocomplete';
import AttachmentsField from '../common/attachments-field';
import { validateExpenseRequestForm } from '../common/validators';
import {
  AttachmentEntityType,
  PaidBy,
  VAT_RATE,
  expenseCategoryOptions,
} from '../common/options';
import { getMySuppliers } from '../../../api/requests/supplier';
import { createExpenseRequest } from '../../../api/requests/requests';
import { getErrorMessage } from '../../../shared/services/error-handler';
import { Routes } from '../../../api/routes';

import '../Requests.scss';

const today = () => new Date().toISOString().slice(0, 10);

const defaultFormData = {
  description: { value: '', error: false },
  expense_date: { value: today(), error: false },
  invoice_number: { value: '', error: false },
  category: { value: '', error: false },
  total_amount: { value: '', error: false },
  paid_by: { value: PaidBy.agent, error: false },
  account_holder_name: { value: '', error: false },
  bank_name: { value: '', error: false },
  bank_number: { value: '', error: false },
  bank_branch_number: { value: '', error: false },
  bank_account_number: { value: '', error: false },
};

const payerOptions = [
  {
    value: PaidBy.yeuls,
    tTitle: 'paid-by-yeuls',
    tHint: 'paid-by-yeuls-hint',
  },
  {
    value: PaidBy.agent,
    tTitle: 'paid-by-me',
    tHint: 'paid-by-me-hint',
  },
];

const ExpenseRequest = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const [formData, setFormData] = useState(defaultFormData);
  const [supplier, setSupplier] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [includesVat, setIncludesVat] = useState(true);
  const [receiptError, setReceiptError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isPaidByYeuls = formData.paid_by.value === PaidBy.yeuls;

  const onSupplierSelect = (selected) => {
    setSupplier(selected);
  };

  const onPayerSelect = (value) =>
    setFormData((current) => ({
      ...current,
      paid_by: { value, error: false },
    }));

  const onSubmit = async () => {
    const hasReceipt = attachments.length > 0;
    setReceiptError(!hasReceipt);

    if (!validateExpenseRequestForm(formData, setFormData) || !hasReceipt) {
      enqueueSnackbar(t('fill-required-fields'), { variant: 'warning' });
      return;
    }

    setSubmitting(true);

    try {
      await createExpenseRequest({
        supplier_id: supplier?.id || undefined,
        description: formData.description.value,
        expense_date: formData.expense_date.value,
        invoice_number: formData.invoice_number.value,
        category: formData.category.value,
        total_amount: Number.parseFloat(formData.total_amount.value),
        includes_vat: includesVat,
        paid_by: formData.paid_by.value,
        account_holder_name: isPaidByYeuls
          ? formData.account_holder_name.value
          : undefined,
        bank_name: isPaidByYeuls ? formData.bank_name.value : undefined,
        bank_number: isPaidByYeuls ? formData.bank_number.value || undefined : undefined,
        bank_branch_number: isPaidByYeuls
          ? formData.bank_branch_number.value
          : undefined,
        bank_account_number: isPaidByYeuls
          ? formData.bank_account_number.value
          : undefined,
        attachment_ids: attachments.map((attachment) => attachment.id),
      });

      enqueueSnackbar(t('expense-request-submitted'), { variant: 'success' });
      history.push(Routes.myRequests);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error) || t('something-went-wrong'), {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RequestPageLayout
      tTitle="expense-request-title"
      tSubtitle="expense-request-page-subtitle"
      tSubmitLabel="submit-expense-request"
      submitClassName="expense"
      onSubmit={onSubmit}
      submitting={submitting}
    >
      <RequestSection tTitle="attach-receipt">
        <AttachmentsField
          entityType={AttachmentEntityType.expenseRequest}
          attachments={attachments}
          setAttachments={setAttachments}
          required
          tTitle="attach-receipt-dropzone-title"
          tHint="attach-receipt-dropzone-hint"
          tError="receipt-is-required"
          hasError={receiptError}
        />
      </RequestSection>

      <RequestSection tTitle="supplier-service-provider">
        <AgentEntityAutocomplete
          fetchOptions={getMySuppliers}
          labelKey="supplier_name"
          tPlaceholder="search-supplier-placeholder"
          tEmptyMessage="no-suppliers-linked-to-you"
          value={supplier}
          onSelect={onSupplierSelect}
        />
      </RequestSection>

      <RequestSection tTitle="expense-details">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RequestInput
              field="description"
              tLabel="expense-description"
              required
              tPlaceholder="expense-description-placeholder"
              tHelper="expense-description-helper"
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RequestInput
              field="expense_date"
              tLabel="date"
              required
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RequestInput
              field="invoice_number"
              tLabel="invoice-number"
              required
              tPlaceholder="invoice-number-placeholder"
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RequestInput
              field="category"
              tLabel="category"
              required
              tPlaceholder="select-category"
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
              options={expenseCategoryOptions}
            />
          </Grid>
          <Grid item xs={12}>
            <RequestInput
              field="total_amount"
              tLabel="total-amount-incl-vat"
              required
              tHelper="total-amount-helper"
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₪</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <RequestField tLabel="includes-vat" >
              <div>
                <Switch
                  color="primary"
                  checked={includesVat}
                  onChange={(event) => setIncludesVat(event.target.checked)}
                />
                <span>{t('includes-vat-rate', { rate: VAT_RATE })}</span>
              </div>
            </RequestField>
          </Grid>
        </Grid>
      </RequestSection>

      <RequestSection tTitle="who-pays-for-the-expense">
        <div className="request-payer-options">
          {payerOptions.map((option) => (
            <button
              type="button"
              key={option.value}
              className={`request-payer-option ${
                formData.paid_by.value === option.value ? 'selected' : ''
              }`}
              onClick={() => onPayerSelect(option.value)}
            >
              <span className="request-payer-option-title">
                {t(option.tTitle)}
              </span>
              <span className="request-payer-option-hint">
                {t(option.tHint)}
              </span>
            </button>
          ))}
        </div>

        {isPaidByYeuls && (
          <div className="request-bank-details">
            <h3 className="request-bank-details-title">
              {t('bank-account-details-for-payment')}
            </h3>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <RequestInput
                  field="account_holder_name"
                  tLabel="account-holder-name"
                  required
                  tPlaceholder="account-holder-name-placeholder"
                  tError="field-is-required"
                  formData={formData}
                  setFormData={setFormData}
                />
              </Grid>
              <Grid item xs={12}>
                <RequestInput
                  field="bank_name"
                  tLabel="bank-name"
                  required
                  tPlaceholder="bank-name-placeholder"
                  tError="field-is-required"
                  formData={formData}
                  setFormData={setFormData}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RequestInput
                  field="bank_branch_number"
                  tLabel="branch-number"
                  required
                  tPlaceholder="branch-number-placeholder"
                  tError="field-is-required"
                  formData={formData}
                  setFormData={setFormData}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RequestInput
                  field="bank_account_number"
                  tLabel="account-number"
                  required
                  tPlaceholder="account-number-placeholder"
                  tError="field-is-required"
                  formData={formData}
                  setFormData={setFormData}
                />
              </Grid>
            </Grid>
          </div>
        )}
      </RequestSection>
    </RequestPageLayout>
  );
};

export default ExpenseRequest;
